import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DotsSixVertical, FloppyDisk, ArrowRight, Plus, Trash, UsersThree } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { promptSuperPassword } from "@/components/admin/SuperPasswordDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Normalise incoming group shape. Backend can return either
 *   { group_name, players: [{id, name}] }   (raw DB doc)
 * OR
 *   { name, player_ids: [...], players: [...] }   (enriched GET /groups response)
 */
function normaliseGroups(groups) {
  if (!groups) return [];
  return groups.map((g, idx) => {
    const group_name = g.group_name || g.name || String.fromCharCode(65 + idx);
    const rawPlayers = g.players || g.player_ids || [];
    const player_ids = rawPlayers
      .map(p => (typeof p === "string" ? p : p?.id))
      .filter(Boolean);
    return { group_name, player_ids };
  });
}

export default function GroupEditor({ tournamentId, initialGroups, allPlayers, onSaved }) {
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState([]); // [{playerId, fromGroup}]
  const [moveToGroup, setMoveToGroup] = useState("");

  useEffect(() => {
    setGroups(normaliseGroups(initialGroups));
  }, [initialGroups]);

  const getPlayerName = (pid) => allPlayers?.find(x => x.id === pid)?.name || pid.substring(0, 8);

  const assignedIds = groups.flatMap(g => g.player_ids);
  const unassigned = allPlayers?.filter(p => !assignedIds.includes(p.id)) || [];

  const isSelected = (pid) => selectedPlayers.some(s => s.playerId === pid);
  const togglePlayer = (pid, fromGroup) => {
    setSelectedPlayers(prev =>
      prev.some(s => s.playerId === pid)
        ? prev.filter(s => s.playerId !== pid)
        : [...prev, { playerId: pid, fromGroup }]
    );
  };
  const clearSelection = () => { setSelectedPlayers([]); setMoveToGroup(""); };

  const executeMove = () => {
    if (!selectedPlayers.length || !moveToGroup) return;
    const movingIds = new Set(selectedPlayers.map(s => s.playerId));
    setGroups(prev => prev.map(g => {
      if (g.group_name === moveToGroup) {
        const existing = g.player_ids.filter(id => !movingIds.has(id));
        return { ...g, player_ids: [...existing, ...movingIds] };
      }
      return { ...g, player_ids: g.player_ids.filter(id => !movingIds.has(id)) };
    }));
    toast.success(`Moved ${selectedPlayers.length} player(s) to Group ${moveToGroup}`);
    clearSelection();
  };

  const addPlayerToGroup = (playerId, groupName) => {
    setGroups(prev => prev.map(g =>
      g.group_name === groupName
        ? { ...g, player_ids: [...g.player_ids, playerId] }
        : g
    ));
  };

  const removePlayerFromGroup = (playerId, groupName) => {
    setGroups(prev => prev.map(g =>
      g.group_name === groupName
        ? { ...g, player_ids: g.player_ids.filter(id => id !== playerId) }
        : g
    ));
    setSelectedPlayers(prev => prev.filter(s => s.playerId !== playerId));
  };

  const addGroup = () => {
    const used = new Set(groups.map(g => g.group_name));
    let nextLetter = "A";
    for (let i = 0; i < 26; i++) {
      const c = String.fromCharCode(65 + i);
      if (!used.has(c)) { nextLetter = c; break; }
    }
    setGroups(prev => [...prev, { group_name: nextLetter, player_ids: [] }]);
  };

  const removeGroup = (groupName) => {
    setGroups(prev => prev.filter(g => g.group_name !== groupName));
  };

  const saveGroups = async (force = false, pwHeader = null) => {
    if (!groups.length) { toast.error("Create at least one group"); return; }
    setSaving(true);
    try {
      const payload = {
        groups: groups.map(g => ({ group_name: g.group_name, player_ids: g.player_ids })),
        force,
      };
      const cfg = { withCredentials: true };
      if (pwHeader) cfg.headers = { "X-Super-Password": pwHeader };
      const res = await axios.put(`${API}/tournaments/${tournamentId}/groups`, payload, cfg);
      toast.success(res.data.message || "Groups saved");
      if (onSaved) onSaved(res.data.groups);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (err.response?.status === 400 && /force=true/i.test(detail) && !force) {
        const ok = window.confirm(
          "⚠️ DESTRUCTIVE ACTION — DATA WILL BE LOST\n\n" +
          detail.replace(/Pass force=true to override.*$/, "") +
          "\n\nThis will:\n" +
          "  • Permanently delete completed group-stage match results\n" +
          "  • Wipe the knockout bracket if one exists\n" +
          "  • NOT roll back any player ELO/points already awarded\n\n" +
          "Are you absolutely sure? (Use Admin → Settings → ELO Repair afterwards to fix any drift.)"
        );
        if (!ok) { toast.warning("Groups not changed"); return; }
        const pw = await promptSuperPassword({
          title: "Super-password required",
          description: "Force-rebuilding groups erases completed match data. Enter the super password to proceed.",
          actionLabel: "Force Save Groups",
        });
        if (!pw) { toast.warning("Groups not changed"); return; }
        return saveGroups(true, pw);
      } else {
        toast.error(detail || "Failed to save groups");
      }
    } finally {
      setSaving(false);
    }
  };

  // Pool of target groups: any group that isn't the sole origin when all selected are from the same group
  const fromGroupsSet = new Set(selectedPlayers.map(s => s.fromGroup));
  const targetGroupOptions = groups.filter(g => !(fromGroupsSet.size === 1 && fromGroupsSet.has(g.group_name)));

  return (
    <Card className="bg-card border-border" data-testid="group-editor">
      <CardHeader className="pb-2 flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-base flex items-center gap-2">
          <UsersThree className="w-5 h-5 text-secondary" weight="fill" />
          Group Editor
        </CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={addGroup} className="h-7 text-xs" data-testid="add-group-btn">
            <Plus className="w-3 h-3 mr-1" />Add Group
          </Button>
          <Button size="sm" onClick={saveGroups} disabled={saving} className="h-7 text-xs" data-testid="save-groups-btn">
            <FloppyDisk className="w-3 h-3 mr-1" />{saving ? "Saving..." : "Save Groups"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Bulk-move action bar — appears when at least one player is selected */}
        {selectedPlayers.length > 0 && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-secondary/10 border border-secondary/30 flex-wrap" data-testid="move-player-bar">
            <span className="text-xs flex-1 min-w-[140px]">
              <strong>{selectedPlayers.length}</strong> player{selectedPlayers.length > 1 ? "s" : ""} selected · move to:
            </span>
            <Select value={moveToGroup} onValueChange={setMoveToGroup}>
              <SelectTrigger className="h-7 w-28 text-xs bg-background border-border" data-testid="move-to-group-select">
                <SelectValue placeholder="Group…" />
              </SelectTrigger>
              <SelectContent>
                {targetGroupOptions.length === 0 ? (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">Add another group first</div>
                ) : targetGroupOptions.map(g => (
                  <SelectItem key={g.group_name} value={g.group_name}>
                    Group {g.group_name} ({g.player_ids.length})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" className="h-7 text-xs" onClick={executeMove} disabled={!moveToGroup} data-testid="confirm-move-btn">
              <ArrowRight className="w-3 h-3 mr-1" />Move
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={clearSelection} data-testid="clear-move-btn">
              Clear
            </Button>
          </div>
        )}

        {/* Groups grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {groups.map(group => (
            <div key={group.group_name} className="border border-border rounded-lg p-3 bg-muted/10" data-testid={`group-${group.group_name}`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-heading font-bold text-sm">
                  Group {group.group_name}
                  <span className="text-muted-foreground font-normal ml-1">({group.player_ids.length})</span>
                </h4>
                {group.player_ids.length === 0 && (
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" onClick={() => removeGroup(group.group_name)}>
                    <Trash className="w-3 h-3" />
                  </Button>
                )}
              </div>
              <div className="space-y-1">
                {group.player_ids.map(pid => (
                  <label
                    key={pid}
                    className={`flex items-center gap-2 p-1.5 rounded text-xs cursor-pointer transition-colors ${
                      isSelected(pid) ? "bg-secondary/20 border border-secondary/40" : "hover:bg-muted/30 border border-transparent"
                    }`}
                    data-testid={`player-row-${pid}`}
                  >
                    <Checkbox
                      checked={isSelected(pid)}
                      onCheckedChange={() => togglePlayer(pid, group.group_name)}
                      data-testid={`select-${pid}`}
                    />
                    <DotsSixVertical className="w-3 h-3 text-muted-foreground" />
                    <span className="flex-1 font-medium truncate">{getPlayerName(pid)}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); removePlayerFromGroup(pid, group.group_name); }}
                      className="text-destructive hover:text-destructive/80"
                      data-testid={`remove-${pid}`}
                      aria-label="Remove from group"
                    >
                      <Trash className="w-3 h-3" />
                    </button>
                  </label>
                ))}
                {group.player_ids.length === 0 && (
                  <p className="text-[11px] text-muted-foreground text-center py-2">Empty group</p>
                )}
              </div>

              {/* Quick-add single unassigned player */}
              {unassigned.length > 0 && (
                <div className="mt-2 pt-2 border-t border-border/50">
                  <Select onValueChange={v => addPlayerToGroup(v, group.group_name)}>
                    <SelectTrigger className="h-7 text-[10px] bg-background border-border">
                      <SelectValue placeholder="+ Add player" />
                    </SelectTrigger>
                    <SelectContent>
                      {unassigned.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          ))}
        </div>

        {unassigned.length > 0 && (
          <div className="pt-3 border-t border-border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Unassigned Players ({unassigned.length})
            </p>
            <div className="flex flex-wrap gap-1.5">
              {unassigned.map(p => (
                <Badge key={p.id} variant="outline" className="text-xs cursor-default">
                  {p.name}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {groups.length === 0 && (
          <div className="text-center py-6 text-muted-foreground text-sm">
            <UsersThree className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>No groups yet. Use the AI Assistant or click "Add Group" to create groups.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
